import type { Dispatch, SetStateAction } from 'react';
import type React from 'react';
import { useEffect } from 'react';
import { filter, includes, range } from 'lodash-es';

import { Box, Card, Paragraph, TextButton } from '@zillow/constellation-10';
import { IconPlusCircleFilled } from '@zillow/constellation-icons';
import { useRentalManagerUser } from '@zg-rentals/rental-manager-env';
import { useLeasingPropertyWebApiHandler_getApplicantsForProperty } from '@zg-rentals/ts-leasing-client-react-query';

import PersonAndApplicantInformation from './PeopleInformation/PersonAndApplicantInformation';
import { PeopleSection } from '../../../models/index';
import { Field } from '../../../models';
import { PERSON } from '../../../constants/index';
import { PERSON_TYPE } from '../../../constants/stringConstants';
import type { Application } from '../../../models/Types';
import ApplicantSelector from './PeopleInformation/ApplicantSelector';
import { EmailValidator } from '../../../utils/validatorTypes';
import { formatPhoneNumber } from '../../Widgets/PhoneInput';
import { useLeasingUrlParams } from '../../../hooks/useLeasingUrlParams';
import { useLeaseQuery } from '../../../utils/queries';

type Props = {
  isOptional: boolean;
  initialPeople: number;
  maximumPeople: number;
  setSectionValues: (keyValuePairs: object) => void;
  setSection: (section: PeopleSection) => void;
  section: PeopleSection;
  setHasError?: Dispatch<SetStateAction<boolean>>;
};

function PeopleAndApplicantInformation({
  initialPeople,
  maximumPeople,
  section,
  setSection,
  setSectionValues,
  setHasError,
}: Props) {
  const { user } = useRentalManagerUser();
  const landlordEmail = user.email;
  const { leaseId, propertyId } = useLeasingUrlParams();

  const leaseQuery = useLeaseQuery(leaseId);
  const lease = leaseQuery.data;
  const isCosmos = !!lease?.journeyId;

  const { data: applicationsResponse } = useLeasingPropertyWebApiHandler_getApplicantsForProperty({
    propertyId,
  });
  const applicationList = (applicationsResponse?.applications || []) as unknown as Array<Application>;

  const maxPeople = maximumPeople || (section as PeopleSection).maxIndexedFieldsThatCanBeAdded;

  const totalPeople = (section as PeopleSection).getTotalIndexes();
  const canAddApplicant = totalPeople === 0;

  useEffect(() => {
    if (!isCosmos && !(section instanceof PeopleSection)) {
      const peopleSection = new PeopleSection((section as PeopleSection).initialParams, PERSON_TYPE.TENANT);
      // Use a for loop with a fixed iteration count to prevent infinite loops
      // when indexedFieldTypes is empty (e.g., in Storybook with empty fields)
      const currentCount = peopleSection.getTotalIndexes() ?? 0;
      const iterationsNeeded = initialPeople - currentCount;
      for (let i = 0; i < iterationsNeeded; i++) {
        peopleSection.addOneIndex();
      }
      setSection(peopleSection);
    }
  }, [isCosmos, section, initialPeople, setSection]);

  function setEmailValidators(index, value, validatedField?: Field): boolean {
    const { fieldMappings } = section as PeopleSection;

    const isEmailValid = EmailValidator.isValid(
      section.getFieldValue(`${PERSON_TYPE.TENANT}${index}_${PERSON.FIELD_NAME.EMAIL_ADDRESS}`),
    );

    if (!isEmailValid) {
      setHasError?.(true);
      return false;
    }

    const isEmailDuplicate = includes(
      filter(
        fieldMappings,
        (field) =>
          field.fieldName.includes(PERSON.FIELD_NAME.EMAIL_ADDRESS) && !field.fieldName.includes(index.toString()),
      ).map((field) => field.getJsonValue().toLowerCase()),
      value.toLowerCase(),
    );

    if (isEmailDuplicate) {
      (
        validatedField ?? section.getField(`${PERSON_TYPE.TENANT}${index}_${PERSON.FIELD_NAME.EMAIL_ADDRESS}`)
      ).setErrorMessage(`Emails for tenants must be unique`);
      setHasError?.(true);
      return false;
    }

    const isTenantEmailSameAsTheLandlord =
      section.getFieldValue(`${PERSON_TYPE.TENANT}${index}_${PERSON.FIELD_NAME.EMAIL_ADDRESS}`) === landlordEmail;

    if (isTenantEmailSameAsTheLandlord) {
      (
        validatedField ?? section.getField(`${PERSON_TYPE.TENANT}${index}_${PERSON.FIELD_NAME.EMAIL_ADDRESS}`)
      ).setErrorMessage(`Tenant cannot have the same email as the landlord`);
      setHasError?.(true);
      return false;
    }

    setHasError?.(false);
    return true;
  }

  function addOneIndex(): void {
    const updatedSection = (section as PeopleSection).clone() as PeopleSection;
    updatedSection.addOneIndex();
    setSection(updatedSection);
  }

  function onAddApplicants(application: Application) {
    const { applicants, applicationId, rentalProtectionEligibility } = application;
    const updatedSection = (section as PeopleSection).clone() as PeopleSection;

    applicants.forEach((applicant) => {
      updatedSection.addOneIndex();
      const latestIndex = updatedSection.getTotalIndexes() - 1;
      const { firstName, email, phone, lastName } = applicant;

      updatedSection.setFieldValues({
        [`${PERSON_TYPE.TENANT}${latestIndex}_${PERSON.FIELD_NAME.FIRST_NAME}`]: firstName,
        [`${PERSON_TYPE.TENANT}${latestIndex}_${PERSON.FIELD_NAME.LAST_NAME}`]: lastName,
        [`${PERSON_TYPE.TENANT}${latestIndex}_${PERSON.FIELD_NAME.EMAIL_ADDRESS}`]: email,
        [`${PERSON_TYPE.TENANT}${latestIndex}_${PERSON.FIELD_NAME.PHONE_NUMBER}`]: formatPhoneNumber(phone || ''),
        [`${PERSON_TYPE.TENANT}${latestIndex}_${PERSON.FIELD_NAME.APPLICATION_ID}`]: applicationId,
        [`${PERSON_TYPE.TENANT}${latestIndex}_${PERSON.FIELD_NAME.RENTAL_PROTECTION_STATUS}`]:
          rentalProtectionEligibility,
      });
    });

    setSection(updatedSection);
  }

  function onFieldChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    setSectionValues({
      [event.currentTarget.dataset.fieldName]: event.currentTarget.value,
    });
  }

  function onRemovePerson(index: number): void {
    const updatedSection = (section as PeopleSection).clone() as PeopleSection;
    const application = updatedSection.getApplication(applicationList);
    if (index === 0 && application) {
      for (let i = 0; i < application.applicants.length; i++) {
        updatedSection.removeOneIndex(0);
      }
    } else {
      updatedSection.removeOneIndex(index);
    }
    setSection(updatedSection);
  }

  function renderPerson(index: number, renderCard: boolean, groupEndIndex?: number): React.ReactNode {
    const isEnabled = section.getFieldValue(Field.getIndexedFieldName(`${PERSON_TYPE.TENANT}_isEnabled`, index));

    if (!isEnabled) {
      return null;
    }

    const firstNameField = (section as PeopleSection).getIndexedFieldGroup(
      `${PERSON_TYPE.TENANT}_${PERSON.FIELD_NAME.FIRST_NAME}`,
    )[index];
    const lastNameField = (section as PeopleSection).getIndexedFieldGroup(
      `${PERSON_TYPE.TENANT}_${PERSON.FIELD_NAME.LAST_NAME}`,
    )[index];
    const emailField = (section as PeopleSection).getIndexedFieldGroup(
      `${PERSON_TYPE.TENANT}_${PERSON.FIELD_NAME.EMAIL_ADDRESS}`,
    )[index];

    const mailingAddressFields = (section as PeopleSection).getIndexedFieldGroup(
      `${PERSON_TYPE.TENANT}_${PERSON.FIELD_NAME.MAILING_ADDRESS}`,
    );
    const mailingAddressField = mailingAddressFields
      ? (section as PeopleSection).getIndexedFieldGroup(`${PERSON_TYPE.TENANT}_${PERSON.FIELD_NAME.MAILING_ADDRESS}`)[
          index
        ]
      : null;

    const phoneNumberFields = (section as PeopleSection).getIndexedFieldGroup(
      `${PERSON_TYPE.TENANT}_${PERSON.FIELD_NAME.PHONE_NUMBER}`,
    );
    const phoneNumberField = phoneNumberFields
      ? (section as PeopleSection).getIndexedFieldGroup(`${PERSON_TYPE.TENANT}_${PERSON.FIELD_NAME.PHONE_NUMBER}`)[
          index
        ]
      : null;

    const application = (section as PeopleSection).getApplication(applicationList);

    section.setCustomFieldValidator(
      `${PERSON_TYPE.TENANT}${index}_${PERSON.FIELD_NAME.EMAIL_ADDRESS}`,
      setEmailValidators.bind(null, index),
    );

    function personAndApplicantInformation(): React.ReactNode {
      return (
        <PersonAndApplicantInformation
          index={index}
          onFieldChange={onFieldChange}
          onRemovePerson={() => onRemovePerson(index)}
          firstNameField={firstNameField}
          firstNameValue={firstNameField.enteredValue}
          lastNameField={lastNameField}
          lastNameValue={lastNameField.enteredValue}
          phoneNumberField={phoneNumberField}
          phoneNumberValue={phoneNumberField && phoneNumberField.enteredValue}
          emailField={emailField}
          emailValue={emailField.enteredValue}
          mailingAddressField={mailingAddressField}
          mailingAddressValue={mailingAddressField && mailingAddressField.enteredValue}
          personTypeLabel={PERSON_TYPE.TENANT}
          application={application}
          groupEndIndex={groupEndIndex}
        />
      );
    }

    /*
    *Note that enteredValues are being passed in separately from their respective fields.
    This is done so that the PersonInformation can be implemented as a PureComponent. Since
    the fields are object references that don't change between updates, updating
    them does not trigger updates since the references remain the same.

    Passing the enteredValues separately triggers the updates.
    */
    return renderCard ? (
      <Card tone="neutral" elevated={false} css={{ marginBottom: 'layout.tight' }}>
        {personAndApplicantInformation()}
      </Card>
    ) : (
      personAndApplicantInformation()
    );
  }

  if (totalPeople == null) {
    // This condition is true on first render before the Section object
    // is transitioned to a PeopleSection object in the constructor.
    return null;
  }

  function renderPeople(): React.ReactNode {
    const application = (section as PeopleSection).getApplication(applicationList);
    if (!application) {
      return (
        <>
          {range(0, totalPeople).map((index: number) => (
            <div data-test="person-container" key={index}>
              {renderPerson(index, true)}
            </div>
          ))}
        </>
      );
    }
    const applicantsCount = application.applicants.length;
    return (
      <>
        <Card tone="neutral" elevated={false} css={{ marginBottom: 'layout.tight' }}>
          {range(0, applicantsCount).map((index: number) => (
            <div data-test="person-container" key={index}>
              {renderPerson(index, false, applicantsCount)}
            </div>
          ))}
        </Card>
        {range(applicantsCount, totalPeople).map((index: number) => (
          <div data-test="person-container" key={index}>
            {renderPerson(index, true)}
          </div>
        ))}
      </>
    );
  }

  function renderAddAdditionalPeopleButton(): React.ReactNode {
    const applicationDropdownVisible = applicationList.length > 0;

    return (
      <>
        {maxPeople > 1 && (
          <Box
            css={{
              marginTop: 'layout.tight',
              marginBottom: 'layout.tight',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'start',
              flexDirection: { base: 'column', lg: 'row' },
            }}
          >
            {totalPeople < maxPeople ? (
              <>
                {applicationDropdownVisible && (
                  <ApplicantSelector
                    applicationList={applicationList}
                    onAddApplicants={onAddApplicants}
                    disabled={!canAddApplicant}
                  />
                )}
                <TextButton data-test="add-another" icon={<IconPlusCircleFilled />} onClick={addOneIndex}>
                  Add new {PERSON_TYPE.TENANT}
                </TextButton>
              </>
            ) : (
              <Paragraph data-test="maximum-reached">
                You’ve reached {`${maxPeople}`} {PERSON_TYPE.TENANT}s, which is the maximum allowed.
              </Paragraph>
            )}
          </Box>
        )}
      </>
    );
  }

  return (
    <>
      <Paragraph css={{ marginBottom: 'layout.tight' }}>
        Select the tenant to add to the lease. If they didn’t submit a Zillow application, they may not be listed, but
        you can add their information manually.
      </Paragraph>

      {renderPeople()}
      {renderAddAdditionalPeopleButton()}
    </>
  );
}

export default PeopleAndApplicantInformation;
